/* ============================================================
   The Find the Lady table: three cards, dealt, turned, swapped, picked.
   Shared by the email pop-up (find-the-lady.js), which plans its own swaps,
   and the signed-in daily game (lady-daily.js), which is handed its swaps by
   the server and only draws them. Styles: the "find the lady" block in
   base.css.

   HONEST BY CONSTRUCTION
   Each card is one DOM element that carries its own face from the moment
   it is dealt. A swap animates two of those elements to each other's place
   and records the move in `slotOf`; nothing is ever re-labelled, re-dealt or
   re-rendered. So there is no state in which the queen could be anywhere
   other than where the animation left her.
   ============================================================ */
(function () {
  'use strict';

  /* Faces are the CC0 English-pattern court cards (Dmitry Fomin, Wikimedia
     Commons); each art class maps to its image in base.css. */
  var CARDS = [
    { art: 'js', name: 'jack of spades', queen: false },
    { art: 'qh', name: 'queen of hearts', queen: true },
    { art: 'kc', name: 'king of clubs', queen: false }
  ];
  var STEP = 3250 / 27;                /* card 27% of the table, gap 5.5%: a seat is 32.5/27 of a card */
  var SLOT_NAMES = ['left', 'middle', 'right'];

  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function place(slot, y, s) {
    return 'translateX(' + (slot * STEP) + '%) translateY(' + (y || 0) + '%) scale(' + (s || 1) + ')';
  }

  function FtlTable(table, opts) {
    opts = opts || {};
    var reduced = !!opts.reduced;
    var els = [], slotOf = [], swapsDone = 0, picking = null;

    function cardAt(slot) {
      for (var i = 0; i < els.length; i++) if (slotOf[i] === slot) return i;
      return -1;
    }
    function flip(el, up) { el.classList.toggle('is-up', up); }

    function deal() {
      table.innerHTML = '';
      table.classList.remove('is-live');
      els = []; slotOf = []; swapsDone = 0;
      CARDS.forEach(function (c, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ftl-card is-up';
        b.disabled = true;
        if (c.queen) b.dataset.queen = '1';
        b.innerHTML =
          '<span class="ftl-card__flip" aria-hidden="true">' +
            '<span class="ftl-card__back"></span>' +
            '<span class="ftl-card__face ftl-card__face--' + c.art + '"></span>' +
          '</span>';
        b.style.transform = place(i);
        b.setAttribute('aria-label', c.name);
        table.appendChild(b);
        els.push(b);
        slotOf.push(i);
      });
    }

    function swap(pair, dur) {
      var a = cardAt(pair[0]), b = cardAt(pair[1]);
      var ea = els[a], eb = els[b];
      var sa = pair[0], sb = pair[1];
      var lift = reduced ? 6 : 12;
      /* One card passes over (lifted, in front), the other under. */
      ea.style.zIndex = 3; eb.style.zIndex = 1;
      var ease = 'cubic-bezier(.45, .05, .35, 1)';
      var ka = [
        { transform: place(sa) },
        { transform: place((sa + sb) / 2, -lift, reduced ? 1 : 1.05), offset: 0.5 },
        { transform: place(sb) }
      ];
      var kb = [
        { transform: place(sb) },
        { transform: place((sa + sb) / 2, lift, reduced ? 1 : 0.95), offset: 0.5 },
        { transform: place(sa) }
      ];
      /* Record the move first, then show it: the record and the picture are
         the same two elements going to the same two seats. */
      slotOf[a] = sb; slotOf[b] = sa;
      swapsDone++;
      var done = [ea, eb].map(function (el, i) {
        var to = i === 0 ? sb : sa;
        if (!el.animate) { el.style.transform = place(to); return wait(dur); }
        var anim = el.animate(i === 0 ? ka : kb, { duration: dur, easing: ease });
        /* A browser that stops painting (tab in the background) can hold an
           animation's promise forever; the timeout keeps the table moving. */
        var end = function () { el.style.transform = place(to); anim.cancel(); };
        return Promise.race([anim.finished, wait(dur + 400)]).then(end, end);
      });
      return Promise.all(done).then(function () { ea.style.zIndex = ''; eb.style.zIndex = ''; });
    }

    /* Show the faces, turn them, then play the swaps at the given speeds. */
    function shuffle(plan, durs, rest) {
      rest = reduced ? 160 : (rest == null ? 70 : rest);
      return wait(1100).then(function () {
        els.forEach(function (el) { flip(el, false); el.setAttribute('aria-label', 'face-down card'); });
        if (opts.onTurned) opts.onTurned();
        return wait(reduced ? 700 : 600);
      }).then(function () {
        return plan.reduce(function (p, pair, i) {
          var dur = reduced ? Math.max(durs[i], 900) : durs[i];
          return p.then(function () { return swap(pair, dur); })
                  .then(function () { return wait(rest); });
        }, Promise.resolve());
      });
    }

    function ready(onPick) {
      picking = onPick;
      /* Tab and arrow order follow the seats as they now stand. */
      for (var s = 0; s < 3; s++) {
        var el = els[cardAt(s)];
        table.appendChild(el);
        el.disabled = false;
        el.tabIndex = s === 1 ? 0 : -1;
        el.setAttribute('aria-label', SLOT_NAMES[s] + ' card');
      }
      table.classList.add('is-live');
      els[cardAt(1)].focus({ preventScroll: true });
    }

    function lock() {
      picking = null;
      table.classList.remove('is-live');
      els.forEach(function (c) { c.disabled = true; });
    }

    /* Turn the picked card, then the rest, and ring the queen. */
    function reveal(el, found) {
      el.classList.add('is-picked');
      flip(el, true);
      var queen = els.filter(function (c) { return c.dataset.queen === '1'; })[0];
      return wait(650).then(function () {
        els.forEach(function (c) {
          flip(c, true);
          c.setAttribute('aria-label', CARDS[els.indexOf(c)].name + ', ' + SLOT_NAMES[slotOf[els.indexOf(c)]]);
        });
        queen.classList.add(found ? 'is-found' : 'is-lady');
      });
    }

    table.addEventListener('keydown', function (e) {
      if (!table.classList.contains('is-live')) return;
      var cur = els.indexOf(document.activeElement);
      if (cur < 0) return;
      var d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
            : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!d) return;
      e.preventDefault();
      var next = els[cardAt((slotOf[cur] + d + 3) % 3)];
      els.forEach(function (el) { el.tabIndex = -1; });
      next.tabIndex = 0;
      next.focus();
    });

    table.addEventListener('click', function (e) {
      var el = e.target.closest('.ftl-card');
      if (!el || !picking || !table.classList.contains('is-live')) return;
      var fn = picking;
      lock();
      fn(el, slotOf[els.indexOf(el)]);
    });

    return {
      deal: deal, shuffle: shuffle, ready: ready, lock: lock, reveal: reveal,
      swaps: function () { return swapsDone; },
      isQueen: function (el) { return el.dataset.queen === '1'; }
    };
  }

  FtlTable.wait = wait;
  window.FtlTable = FtlTable;
})();
